import { Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Notification } from "../../../../@types/Notification";
import { PatientMessagesTableContent } from "../../../../@types/Patient";
import { ReactComponent as NoMessagesImage } from "../../../../assets/images/woman-computer.svg";
import { InfiniteTable } from "../../../../components/InfiniteTable";
import { RetryButton } from "../../../../components/RetryButton";
import { Table } from "../../../../components/Table";
import { TableSkeleton } from "../../../../components/TableSkeleton";
import { useCloseThread } from "../../../../hooks/mutations/messages";
import { getPatientRegisteredDevices } from "../../../../hooks/queries/messages";
import { useModal } from "../../../../hooks/useModal";
import { useToast } from "../../../../hooks/useToast";
import { mixpanelActions } from "../../../../utils/mixpanel";
import {
  CloseThreadButton,
  StatusText,
} from "../MessagesSection/MessagesSection.styles";
import { desktopTableColumns } from "../MessagesSection/patientMessagesTableColumns";
import { CreateMessageModal } from "./components/CreateMessageModal";
import { MessageThreadModal } from "./components/MessageThreadModal";
import {
  ImageContainer,
  ManageMessagesTitle,
  MessagesTableContainer,
  MessagesTableInnerContainer,
  NoContentMessage,
  TooltipText,
} from "./PatientMessagesTable.style";

type PatientMessagesTableProps = {
  patientId: number;
  patientPhoneNumber: string | undefined;
  content: PatientMessagesTableContent[];
  isLoading?: boolean;
  isError?: boolean;
  highlightedRows: boolean[];
  refreshTable: () => void;
  showSendMessageButton: boolean;
  /**
   * EN-5124: an archived patient cannot exchange messages. The API rejects both sending and
   * replying with 400 PatientArchived, so the UI must not offer either — otherwise staff hit
   * an opaque error, which is how the wrong-patient incident went unnoticed in the first place.
   */
  isPatientArchived?: boolean;
  // Infinite scroll props (optional for backward compatibility)
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  useInfiniteScroll?: boolean;
};

export function PatientMessagesTable({
  patientId,
  patientPhoneNumber,
  isLoading,
  isError,
  content,
  highlightedRows,
  showSendMessageButton,
  isPatientArchived = false,
  refreshTable,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  useInfiniteScroll = false,
}: PatientMessagesTableProps) {
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const { openModal, closeModal } = useModal();
  const [hasRegisteredDevices, setHasRegisteredDevices] = useState(false);

  const patientRegisteredDevices = getPatientRegisteredDevices(patientId);
  const { showToast } = useToast();
  const { mutateAsync: closeThread } = useCloseThread();
  const [closingThreadId, setClosingThreadId] = useState<string | null>(null);

  useEffect(() => {
    if (
      patientRegisteredDevices &&
      patientRegisteredDevices.PatientRegisteredDevices &&
      patientRegisteredDevices.PatientRegisteredDevices.length > 0
    ) {
      setHasRegisteredDevices(true);
    }
  }, [patientRegisteredDevices]);

  /**
   * EN-3040. The Status column's three faces, per the ticket: an open thread offers the CTA, a
   * closed one says so, and a thread whose last staff message never left says that instead.
   *
   * The whole row is clickable (Table.tsx wires onClick on the Tr), so the button has to stop the
   * event — otherwise closing a thread also opens it. Same guard as NotesSection's action cell.
   */
  const handleCloseThread = async (threadId: string) => {
    setClosingThreadId(threadId);

    try {
      await closeThread(threadId);
      mixpanelActions.track("User Action: Close thread");
      showToast(t("patientPage_messages_thread_closedToast"), "success");
      refreshTable();
    } catch {
      showToast(t("patientPage_messages_thread_closeFailed"), "error");
    } finally {
      setClosingThreadId(null);
    }
  };

  const renderStatusCell = (message: PatientMessagesTableContent) => {
    if (message.threadStatus === "Closed") {
      return (
        <StatusText>{t("patientPage_messages_table_statusClosed")}</StatusText>
      );
    }

    if (message.threadStatus === "FailedToSend") {
      return (
        <StatusText failed>
          {t("patientPage_messages_table_statusFailedToSend")}
        </StatusText>
      );
    }

    return (
      <div onClick={(e) => e.stopPropagation()} role="presentation">
        <CloseThreadButton
          type="button"
          disabled={closingThreadId === message.threadId}
          onClick={() => handleCloseThread(message.threadId)}
        >
          {t("patientPage_messages_table_closeThread")}
        </CloseThreadButton>
      </div>
    );
  };

  const parseMessagesToTable = useMemo(() => {
    if (!content) return [];

    return content.map((message: any) => {
      return {
        id: message.id,
        title: message.title,
        sentBy: message.sentBy,
        response: message.response,
        createdBy: message.createdBy,
        responseType: message.responseType,
        responseOptions: message.responseOptions,
        body: message.body,
        isRead: message.isRead,
        responseDate: message.responseDate,
        isAnswered: message.isAnswered,
        freeResponse: message.freeResponse,
        lastMessage: message.lastMessage,
        sentDate: message.sentDate,
        lastUpdated: message.lastUpdated,
        isStaffResponseRead: message.isStaffResponseRead,
        staffName: message.staffName,
        articleId: message.articleId,
        article: message.article,
        threadId: message.threadId,
        isThreadClosed: message.isThreadClosed,
        threadStatus: message.threadStatus,
        messageCount: message.messageCount,
        unreadForStaffCount: message.unreadForStaffCount,
        statusAction: renderStatusCell(message),
      } as unknown as PatientMessagesTableContent;
    });
    // closingThreadId re-renders the CTA into its pending state while the request is in flight.
  }, [content, closingThreadId, t]);

  const handleCreateNoteClose = () => {
    closeModal();
    // Delay 200ms in order to allow the message status to update
    setTimeout(() => {
      refreshTable();
    }, 200);
  };

  const handleOnClose = async (
    notification?: Notification,
    shouldRefresh?: boolean
  ) => {
    closeModal();

    if (shouldRefresh) {
      refreshTable();
    }

    if (notification !== undefined) {
      showToast(notification.message, notification.type);
    }
  };

  const openThread = (threadId: string) => {
    openModal(
      <MessageThreadModal
        threadId={threadId}
        patientId={patientId}
        patientPhoneNumber={patientPhoneNumber}
        hasRegisteredDevices={hasRegisteredDevices}
        isPatientArchived={isPatientArchived}
        showToast={showToast}
        onClose={handleOnClose}
      />,
      {
        // The modal renders its own X: the global one calls closeModal(), and on the Custom step
        // the ticket wants X to come back to the conversation instead of leaving it.
        showCloseButton: false,
        width: "756px",
        height: "100%",
        maxHeight: "873px",
      }
    );
  };

  const handleRowClick = async (
    patientMessageContent: PatientMessagesTableContent
  ) => {
    openThread(patientMessageContent.threadId ?? patientMessageContent.id);
  };

  const handleCloseModal = (
    notificationMessage: string,
    notificationType: "success" | "warning" | "error"
  ) => {
    if (notificationMessage && notificationType) {
      showToast(notificationMessage, notificationType);
    }

    closeModal();
  };

  const handleNewMessage = () => {
    // Defensive: the affordance below is already disabled for archived patients, but the
    // composer must never open for one — the send would just 400.
    if (isPatientArchived) return;

    mixpanelActions.track(`User Action: ClickedNewMessage`);
    openModal(
      <CreateMessageModal
        patientId={patientId}
        patientPhoneNumber={patientPhoneNumber}
        hasRegisteredDevices={hasRegisteredDevices}
        setNotification={handleCloseModal}
        onClose={handleCreateNoteClose}
      />,
      {
        showCloseButton: true,
        width: "auto",
        height: "auto",
      }
    );
  };

  useEffect(() => {
    if (
      window.location.pathname.substring(
        window.location.pathname.lastIndexOf("/") + 1
      ) === "messages"
    ) {
      // The staff email links ?id=<messageId>. Rows are one per thread and carry the LATEST message's
      // id, so a row lookup misses as soon as anyone writes after the email went out. Every thread
      // route accepts a message id, so open the conversation straight from the link.
      const messageId = searchParams.get("id");
      if (messageId) {
        openThread(messageId);
      }
    }
  }, []);

  // Rendered identically in all three branches below, so build it in one place.
  const renderSendMessageButton = () => {
    if (!showSendMessageButton) return null;

    // EN-5124: archived patients cannot be messaged — the API rejects the send with
    // 400 PatientArchived, so show why instead of letting staff hit the error.
    if (isPatientArchived) {
      return (
        <Tooltip
          title={
            <TooltipText>
              {t("patientPage_messages_table_archivedPatient")}
            </TooltipText>
          }
          zIndex={3002}
          color="#4B4B4B"
          arrow={false}
          placement="left"
          styles={{
            body: {
              padding: "12px 16px",
              borderRadius: "4px !important",
            },
          }}
        >
          <ManageMessagesTitle disabled>
            {t("patientPage_messages_table_sendMessage")}
          </ManageMessagesTitle>
        </Tooltip>
      );
    }

    return (
      <ManageMessagesTitle onClick={handleNewMessage}>
        {t("patientPage_messages_send")}
      </ManageMessagesTitle>
    );
  };

  if (isLoading) {
    return (
      <MessagesTableContainer style={{ overflow: "hidden" }}>
        <MessagesTableInnerContainer style={{ overflow: "hidden" }}>
          <TableSkeleton columnsNumber={5} rowsNumber={8} />
        </MessagesTableInnerContainer>
      </MessagesTableContainer>
    );
  }

  if (isError) {
    return (
      <ImageContainer>
        <p>
          <Trans
            i18nKey="patientPage_messages_loadError"
            components={{
              retry: <RetryButton onClick={refreshTable} />,
            }}
          />
        </p>
      </ImageContainer>
    );
  }

  if (content && content.length > 0) {
    // Use InfiniteTable when infinite scroll is enabled
    if (useInfiniteScroll && fetchNextPage) {
      return (
        <>
          {renderSendMessageButton()}
          <MessagesTableContainer>
            <MessagesTableInnerContainer>
              <InfiniteTable
                data={parseMessagesToTable}
                columns={desktopTableColumns}
                highlightedRows={highlightedRows}
                onRowClick={handleRowClick}
                highlightColor={[
                  "var(--colors-table-row-highlighted-background-primary)",
                  "var(--colors-table-row-highlighted-background-secondary)",
                ]}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                skeletonColumns={5}
                skeletonRows={8}
              />
            </MessagesTableInnerContainer>
          </MessagesTableContainer>
        </>
      );
    }

    // Default to regular Table for backward compatibility
    return (
      <>
        {renderSendMessageButton()}
        <MessagesTableContainer>
          <MessagesTableInnerContainer>
            <Table
              data={parseMessagesToTable}
              columns={desktopTableColumns}
              highlightedRows={highlightedRows}
              onRowClick={handleRowClick}
              highlightColor={[
                "var(--colors-table-row-highlighted-background-primary)",
                "var(--colors-table-row-highlighted-background-secondary)",
              ]}
            />
          </MessagesTableInnerContainer>
        </MessagesTableContainer>
      </>
    );
  }

  return (
    <>
      {renderSendMessageButton()}
      <ImageContainer>
        <NoMessagesImage width={181} height={158} />
        <NoContentMessage>{t("patientPage_messages_empty")}</NoContentMessage>
      </ImageContainer>
    </>
  );
}
